import React, { useEffect, useRef } from 'react';
import './CustomCursor.css';

const CustomCursor: React.FC = () => {
  const cursorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = cursorRef.current;
    if (!node) return;
    const cursorElement: HTMLDivElement = node;

    const textTargets = new Set<Element>();
    const mergeables = new Set<Element>();
    const textElements = new Set<Element>();

    // Coleciona alvos de texto (I‑beam) e merge (menu)
    document.querySelectorAll('.is-text-target, [data-cursor="text"]').forEach(el => textTargets.add(el));
    document.querySelectorAll('a, button, .btn, nav a, .mergeable').forEach(el => mergeables.add(el));

    // Coleciona elementos de texto (títulos, parágrafos, etc.)
    document.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, div, li, td, th, label, input[type="text"], textarea, .text-content').forEach(el => {
      // Verificar se o elemento contém texto e não é um botão, link ou mergeable
      if (el.textContent && el.textContent.trim() &&
          !el.classList.contains('btn') &&
          !el.tagName.toLowerCase().includes('button') &&
          !el.tagName.toLowerCase().includes('a') &&
          !el.classList.contains('mergeable')) {
        textElements.add(el);
      }
    });

    let mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    let pos = { x: mouse.x, y: mouse.y };
    let target = { x: mouse.x, y: mouse.y };

    let lockedToEl: Element | null = null;
    let rafId: number | null = null;

    // Helpers para atualização de CSS vars
    const setVar = (k: string, v: string) => cursorElement.style.setProperty(k, v);
    const setXY = (x: number, y: number) => {
      setVar('--cursor-x', x + 'px');
      setVar('--cursor-y', y + 'px');
    };

    // Lerp simples
    function lerp(a: number, b: number, n: number) {
      return (1 - n) * a + n * b;
    }

    function animate() {
      // se estiver fundido a um elemento, segue o centro desse elemento
      if (lockedToEl) {
        const rect = lockedToEl.getBoundingClientRect();
        target.x = rect.left + rect.width / 2;
        target.y = rect.top + rect.height / 2;
        // aproximação um pouco mais rápida quando fundido
        pos.x = lerp(pos.x, target.x, 0.22);
        pos.y = lerp(pos.y, target.y, 0.22);
      } else {
        // segue o rato com suavidade
        pos.x = lerp(pos.x, mouse.x, 0.18);
        pos.y = lerp(pos.y, mouse.y, 0.18);
      }
      setXY(pos.x, pos.y);
      rafId = requestAnimationFrame(animate);
    }

    // Eventos de movimento
    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    // Clique: dar um pequeno "tap" visual
    const handleMouseDown = () => document.body.classList.add('is-down');
    const handleMouseUp = () => document.body.classList.remove('is-down');

    // Ocultar quando sai da janela
    const handleMouseLeave = () => setVar('--cursor-opacity', '0');
    const handleMouseEnter = () => setVar('--cursor-opacity', '1');

    // ————— I‑beam em hover —————
    const handleTextEnter = () => cursorElement.classList.add('is-text');
    const handleTextLeave = () => cursorElement.classList.remove('is-text');

    // ————— I‑beam para elementos de texto —————
    const handleTextElementEnter = () => {
      if (!lockedToEl) { // Só aplica I-beam se não estiver fundido
        cursorElement.classList.add('is-text');
      }
    };
    const handleTextElementLeave = () => {
      cursorElement.classList.remove('is-text');
    };

        // ————— Fusão com itens do menu —————
    function enterMerge(el: Element) {
      lockedToEl = el;
      cursorElement.classList.add('is-merge');

      // Remover I-beam quando fundido
      cursorElement.classList.remove('is-text');

      // Detectar se é um CTA (botão com fundo colorido)
      const isCTA = el.classList.contains('cta-header') ||
                   el.classList.contains('btn') &&
                   ((el as HTMLElement).style.background ||
                    getComputedStyle(el).backgroundColor !== 'rgba(0, 0, 0, 0)' &&
                    getComputedStyle(el).backgroundColor !== 'transparent');

      if (isCTA) {
        cursorElement.classList.add('is-cta');
      }

      const rect = el.getBoundingClientRect();
      const pad = 10; // expande um pouco para cobrir o botão
      setVar('--cursor-w', (rect.width + pad) + 'px');
      setVar('--cursor-h', (rect.height + pad) + 'px');
      const br = getComputedStyle(el).borderRadius || '12px';
      setVar('--cursor-radius', br);
    }

    function leaveMerge() {
      lockedToEl = null;
      cursorElement.classList.remove('is-merge', 'is-cta');
      // voltar ao círculo
      setVar('--cursor-w', 'var(--cursor-size)');
      setVar('--cursor-h', 'var(--cursor-size)');
      setVar('--cursor-radius', '999px');
    }

    const handleMergeEnter = (e: Event) => enterMerge(e.target as Element);
    const handleMergeLeave = () => leaveMerge();

    // Recalcular quando window muda de tamanho/scroll
    const handleScroll = () => {
      if (lockedToEl) enterMerge(lockedToEl);
    };
    const handleResize = () => {
      if (lockedToEl) enterMerge(lockedToEl);
    };

    // Função para recarregar elementos de texto (para conteúdo dinâmico)
    const reloadTextElements = () => {
      // Limpar listeners antigos
      textElements.forEach(el => {
        el.removeEventListener('mouseenter', handleTextElementEnter);
        el.removeEventListener('mouseleave', handleTextElementLeave);
      });
      textElements.clear();

      // Recarregar elementos de texto
      document.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, div, li, td, th, label, input[type="text"], textarea, .text-content').forEach(el => {
        if (el.textContent && el.textContent.trim() &&
            !el.classList.contains('btn') &&
            !el.tagName.toLowerCase().includes('button') &&
            !el.tagName.toLowerCase().includes('a') &&
            !el.classList.contains('mergeable')) {
          textElements.add(el);
          el.addEventListener('mouseenter', handleTextElementEnter);
          el.addEventListener('mouseleave', handleTextElementLeave);
        }
      });
    };

    // Adicionar event listeners
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    // Observer para detectar mudanças no DOM e recarregar elementos de texto
    const observer = new MutationObserver((mutations) => {
      let shouldReload = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldReload = true;
        }
      });
      if (shouldReload) {
        // Debounce para evitar muitas recargas
        setTimeout(reloadTextElements, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Adicionar listeners para elementos de texto
    textTargets.forEach(el => {
      el.addEventListener('mouseenter', handleTextEnter);
      el.addEventListener('mouseleave', handleTextLeave);
    });

    // Adicionar listeners para elementos de texto gerais
    textElements.forEach(el => {
      el.addEventListener('mouseenter', handleTextElementEnter);
      el.addEventListener('mouseleave', handleTextElementLeave);
    });

    // Adicionar listeners para elementos mergeables
    mergeables.forEach(el => {
      el.addEventListener('mouseenter', handleMergeEnter);
      el.addEventListener('mouseleave', handleMergeLeave);
    });

    // Iniciar animação
    animate();

    // Cleanup
    return () => {
      if (rafId) cancelAnimationFrame(rafId);

      observer.disconnect();

      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);

      textTargets.forEach(el => {
        el.removeEventListener('mouseenter', handleTextEnter);
        el.removeEventListener('mouseleave', handleTextLeave);
      });

      textElements.forEach(el => {
        el.removeEventListener('mouseenter', handleTextElementEnter);
        el.removeEventListener('mouseleave', handleTextElementLeave);
      });

      mergeables.forEach(el => {
        el.removeEventListener('mouseenter', handleMergeEnter);
        el.removeEventListener('mouseleave', handleMergeLeave);
      });
    };
  }, []);

  return <div id="cursor" ref={cursorRef} aria-hidden="true"></div>;
};

export default CustomCursor;
